// Story 018 — Sketch widget (W3). Two-tab pane: Sketch (default) | Density.
//
// Sketch tab: SVG polygon outline of the blade subcomponent + small markers
// (squares) at `(radius, chordPercent)`. Marker fill = severity colour
// (data-driven via `defect.classColor`, fallback by `classCategory`).
// Click marker → activates the defect via selectionStore; ring-highlights
// the active defect.
//
// Density tab: four small density curves (one each PS / SS / LE / TE) of
// defect-count distribution along the radius axis. MVP ships a simple
// histogram in lieu of full violins (visx adds 60-80 KB and we don't
// need the symmetry — a kernel-density-bar version reads the same way
// at this scale). Story 018 explicit-out-of-scope says "MVP can do
// histogram if violins balloon"; we exercise that.
import { useMemo, useState } from "react";
import { Box, Stack, Tab, Tabs, Typography, useTheme } from "@mui/material";
import type { Defect } from "@aeriosiq/types";
import { color, typography } from "@aeriosiq/ui";
import { useDefectsQuery } from "../../../defects/queries.js";
import { useSelectionStore } from "../../state/selectionStore.js";
import {
  applyFilters,
  useAnalysisFilterStore,
  type SideKey,
} from "../../state/analysisFilterStore.js";
import { useComponentOutlineQuery } from "../../queries/useComponentOutlineQuery.js";

interface SketchWidgetProps {
  inspectionId: string;
  /** Which component's outline to fetch. If null, FE renders fallback. */
  componentId: string | null;
}

interface SketchableDefect {
  id: string;
  radiusM: number;
  chordPercent: number;
  side: SideKey | null;
  classCategory: number | null;
  classColor: string | null;
}

const SIDE_ORDER: readonly SideKey[] = ["LE", "TE", "PS", "SS"];
const FALLBACK_BLADE_LENGTH_M = 45;

type Palette = (typeof color)["light"] | (typeof color)["dark"];

/**
 * SVG `fill`/`stroke` attributes cannot read a CSS custom property any more
 * than a canvas can — a `var(--x)` there paints nothing. So this widget
 * resolves REAL values off the token set for the theme it is rendered in,
 * exactly once, and hands them to the geometry below.
 */
function usePalette(): Palette {
  const theme = useTheme();
  return color[theme.palette.mode];
}

/**
 * D83's severity ramp, indexed by the class model's grade integer.
 *
 * This used to be a Material Design scale (`#4CAF50` … `#F44336`) — one of
 * FOUR competing five-step ramps in the app, and the only one that wasn't even
 * from the same design language as the rest of the product. It is now the same
 * `severity` token set the bounding-box overlay and every SeverityChip read,
 * so a grade looks the same wherever it is drawn.
 *
 * The grade→slot mapping is `gradeToSeverity`'s absolute branch (1 → info …
 * 5 → critical, `defects/severityDisplay.ts`), which mirrors the gateway's
 * `classCategoryToSeverity`. There is no GREEN step any more: D83 reuses D81's
 * slots and green means `success`, which is not a severity — grade 1 is
 * "informational", which is what the neutral slot says.
 *
 * Out-of-range grades resolve to `undefined` and fall through to the caller's
 * neutral, exactly as the old lookup table did.
 */
function severityRampFor(palette: Palette): Record<number, string> {
  return {
    1: palette.severity.info,
    2: palette.severity.low,
    3: palette.severity.medium,
    4: palette.severity.high,
    5: palette.severity.critical,
  };
}

function projectDefects(raw: readonly Defect[]): readonly SketchableDefect[] {
  return raw
    .map((d) => {
      if (
        d.radiusM == null ||
        d.chordPercent == null ||
        !Number.isFinite(d.radiusM) ||
        !Number.isFinite(d.chordPercent)
      ) {
        return null;
      }
      return {
        id: d.id,
        // Negative radii happen for blades on the opposite side of the hub
        // (Q3 in `.vibe/23`). Flatten to absolute distance from root.
        radiusM: Math.abs(d.radiusM),
        // Clamp into [0, 100] in case of dirty data.
        chordPercent: Math.max(0, Math.min(100, d.chordPercent)),
        side: d.side ?? null,
        classCategory: d.classCategory ?? null,
        classColor: d.classColor ?? null,
      };
    })
    .filter((d): d is SketchableDefect => d !== null);
}

interface SketchPaneProps {
  defects: readonly SketchableDefect[];
  bladeLengthM: number;
  outlineSvg: string | null;
  activeFindingId: string | null;
  onActivate: (id: string) => void;
  /** Optional radius (m from blade root) of the currently-displayed
   *  Viewer image. When provided, a horizontal marker line is drawn at
   *  that radius even on the empty-defects state, so the sketch always
   *  has a "you are here" cue. */
  activeImageRadiusM: number | null;
}

/**
 * R58-L-06 — radius → viewBox y mapping. The SVG viewBox has `y=0` at
 * the top and `y=bladeLengthM` at the bottom, but the physical blade
 * has the root at the BOTTOM of the sketch and the tip at the TOP
 * (matches Aero-Sync's stitched view and the drone-inspection
 * orientation; see PR #58 walkthrough 2026-05-18 [00:41:39]). Map
 * radius r metres → viewBox y so r=0 lands at the bottom edge:
 *
 *   y = bladeLengthM - r
 *
 * Equivalent to `(1 - r / bladeLengthM) * bladeLengthM` (the form the
 * R58-L-06 story spells out). We pick the subtraction form because
 * the viewBox height is literally `bladeLengthM`, so no rescale is
 * needed — every consumer of this helper writes the y attribute as a
 * raw viewBox unit.
 */
function yFromRadius(r: number, bladeLengthM: number): number {
  return bladeLengthM - r;
}

/**
 * R58-L-06 — radius axis tick labels, drawn inside the SVG. We render
 * just the two anchor ticks (`0 m` at the bottom, `bladeLengthM m` at
 * the top) because the SketchWidget panel is narrow and additional
 * intermediate ticks would compete with the defect markers. The text
 * is rendered in viewBox units, but `vector-effect="non-scaling-stroke"`
 * is not enough for `<text>` — instead we lean on the small viewBox
 * unit size (≈1.6 m at the typical render width) and a `font-size`
 * tuned to read at every sensible widget width.
 *
 * Positioned just inside each edge so the text doesn't clip:
 *  - `0 m` near the bottom: y = bladeLengthM − 0.4
 *  - `{bladeLengthM} m` near the top: y = 1.6 (text baseline)
 */
function RadiusTicks({
  bladeLengthM,
}: {
  bladeLengthM: number;
}): React.ReactElement {
  // SVG `<text>` length units default to viewBox units. Because the
  // sketch viewBox is `0..bladeLengthM` (≈45) tall but the rendered
  // SVG is only ~88px wide (SKETCH_WIDTH in AnalysisLayout), any
  // sensible viewBox-unit fontSize collapses to a sub-3-pixel render.
  // We pin the font size in CSS pixels via the `style` prop so the
  // tick labels stay legible regardless of how `meet` scales the
  // rest of the geometry. The text y is still in viewBox units;
  // SVG resolves them through the user-space at paint time.
  const padX = 1.2; // viewBox x-units; ≈ 1 CSS px at the typical widget width
  // Axis tick labels are the LABEL step of the text ramp, not body — quieter
  // than the caption beneath the sketch and still AA on the panel.
  const tickFill = usePalette().text.tertiary;
  return (
    <g aria-hidden="true" data-testid="sketch-radius-ticks">
      <text
        data-testid="sketch-radius-tick"
        x={padX}
        y={yFromRadius(0, bladeLengthM) - 0.6}
        fill={tickFill}
        style={{
          fontSize: "10px",
          fontWeight: 500,
          pointerEvents: "none",
        }}
      >
        0 m
      </text>
      <text
        data-testid="sketch-radius-tick"
        x={padX}
        y={yFromRadius(bladeLengthM, bladeLengthM) + 2.5}
        fill={tickFill}
        style={{
          fontSize: "10px",
          fontWeight: 500,
          pointerEvents: "none",
        }}
      >
        {`${String(bladeLengthM)} m`}
      </text>
    </g>
  );
}

function SketchPane({
  defects,
  bladeLengthM,
  outlineSvg,
  activeFindingId,
  onActivate,
  activeImageRadiusM,
}: SketchPaneProps): React.ReactElement {
  const palette = usePalette();
  const severityRamp = severityRampFor(palette);
  // The fallback silhouette is STRUCTURE, not data (P2) — a placeholder frame
  // standing in for the BE's real outline. It used to be brand blue, which
  // under P3 would now read as "interactive/live", so it takes the sunken
  // surface + the control-edge border instead. The accent is spent on the one
  // thing in this SVG that IS live: the current-image cue.
  const outlineFill = palette.surface.sunken;
  const outlineStroke = palette.borders.control;
  // viewBox: x = chord% (0..100), y = (bladeLengthM - radius) so that
  // the bottom edge is the blade root and the top edge is the tip
  // (R58-L-06, 2026-05-18 walkthrough).
  const viewBox = `0 0 100 ${String(bladeLengthM)}`;
  // Marker side length in viewBox units. We want ≥20 px on screen
  // (Story 018 AC3) — the widget rescales the viewBox to its rendered
  // area, so we pick a marker size that's visible at any reasonable
  // widget width. 1.5 viewBox units works out to ~6 px at the typical
  // widget width and ~15 px on a large desktop; we use the
  // `vector-effect="non-scaling-stroke"` trick to keep the outline
  // crisp regardless of zoom.
  const markerSize = 2;

  // Compute the active-image marker radius once (clamped into the visible
  // band); shared across the empty + populated states so the cue stays
  // even when there are zero sketchable defects.
  // `radiusClampedM` is the human-readable distance from root in metres
  // (used in the "Current image · X m from root" caption); `markerY` is
  // its position in viewBox units AFTER the R58-L-06 axis flip.
  const radiusClampedM =
    activeImageRadiusM != null && Number.isFinite(activeImageRadiusM)
      ? Math.max(0, Math.min(bladeLengthM, Math.abs(activeImageRadiusM)))
      : null;
  const markerY =
    radiusClampedM != null ? yFromRadius(radiusClampedM, bladeLengthM) : null;

  if (defects.length === 0) {
    // Even without defects (and even without a server-side blade outline)
    // we still draw the fallback silhouette + the image-position cue so
    // the panel is never blank. The previous implementation hid the
    // sketch entirely when both inputs were missing, which read as a
    // broken widget — see Christian's note 2026-05-09.
    return (
      <Box sx={{ height: "100%", p: 1.5 }}>
        <Box
          sx={{
            width: "100%",
            height: "calc(100% - 28px)",
            position: "relative",
          }}
        >
          <svg
            viewBox={viewBox}
            preserveAspectRatio="xMidYMid meet"
            style={{ width: "100%", height: "100%" }}
            role="img"
            aria-label="Blade sketch — no defects yet"
            data-testid="sketch-svg"
          >
            {outlineSvg ? (
              // The BE authors outline content in the same `0..bladeLengthM`
              // y-axis it always has, with the root at y=0. R58-L-06 flips
              // the on-screen orientation, so we mirror the outline group
              // vertically around the viewBox midline. Defect markers and
              // tick labels are drawn outside this group with the
              // pre-flipped y (via `yFromRadius`) so their text stays
              // upright.
              <g
                transform={`matrix(1 0 0 -1 0 ${String(bladeLengthM)})`}
                dangerouslySetInnerHTML={{
                  __html: stripSvgWrapper(outlineSvg),
                }}
              />
            ) : (
              <rect
                x={5}
                y={0}
                width={90}
                height={bladeLengthM}
                fill={outlineFill}
                stroke={outlineStroke}
                strokeWidth={0.4}
                vectorEffect="non-scaling-stroke"
                data-testid="sketch-fallback-outline"
              />
            )}
            <RadiusTicks bladeLengthM={bladeLengthM} />
            {markerY != null ? (
              <g data-testid="sketch-image-marker">
                <line
                  x1={0}
                  y1={markerY}
                  x2={100}
                  y2={markerY}
                  stroke={palette.primary}
                  strokeWidth={0.5}
                  strokeDasharray="2 1.5"
                  vectorEffect="non-scaling-stroke"
                  opacity={0.85}
                />
                <circle
                  cx={50}
                  cy={markerY}
                  r={1.4}
                  fill={palette.primary}
                  stroke={palette.surface.default}
                  strokeWidth={0.4}
                  vectorEffect="non-scaling-stroke"
                />
              </g>
            ) : null}
          </svg>
        </Box>
        <Stack
          direction="row"
          justifyContent="space-between"
          sx={{ mt: 0.5, fontSize: 11, color: "text.secondary" }}
          aria-hidden
        >
          {/* Horizontal labels = chord axis (LE → TE). The radius
              labels live INSIDE the SVG as <text> ticks after R58-L-06. */}
          <span>LE 0%</span>
          <span>TE 100%</span>
        </Stack>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", textAlign: "center", mt: 0.5 }}
          role="status"
          aria-live="polite"
        >
          {radiusClampedM != null
            ? `Current image · ${radiusClampedM.toFixed(1)} m from root`
            : "Pick an image to see its position"}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: "100%", p: 1.5 }}>
      <Box
        sx={{
          width: "100%",
          height: "calc(100% - 28px)",
          position: "relative",
        }}
      >
        <svg
          viewBox={viewBox}
          preserveAspectRatio="xMidYMid meet"
          style={{ width: "100%", height: "100%" }}
          role="img"
          aria-label={`Blade sketch with ${String(defects.length)} defects`}
          data-testid="sketch-svg"
        >
          {/* Outline: either trusted backend SVG inlined, or fallback rect.
              The BE-authored outline is mirrored vertically around the
              viewBox midline so the root sits at the bottom (R58-L-06). */}
          {outlineSvg ? (
            <g
              transform={`matrix(1 0 0 -1 0 ${String(bladeLengthM)})`}
              dangerouslySetInnerHTML={{
                __html: stripSvgWrapper(outlineSvg),
              }}
            />
          ) : (
            <rect
              x={5}
              y={0}
              width={90}
              height={bladeLengthM}
              fill={outlineFill}
              stroke={outlineStroke}
              strokeWidth={0.4}
              vectorEffect="non-scaling-stroke"
              data-testid="sketch-fallback-outline"
            />
          )}

          <RadiusTicks bladeLengthM={bladeLengthM} />

          {/* Defect markers */}
          {defects.map((d) => {
            const fill =
              d.classColor ??
              (d.classCategory != null
                ? severityRamp[d.classCategory]
                : null) ??
              // Ungraded: `status.neutral` means exactly "inert / not started",
              // which is what a defect with no grade is. Never a borrowed grade.
              palette.status.neutral;
            const isActive = d.id === activeFindingId;
            return (
              <g
                key={d.id}
                role="button"
                tabIndex={0}
                aria-label={`Defect — radius ${d.radiusM.toFixed(1)} m, chord ${String(Math.round(d.chordPercent))} %, severity ${String(d.classCategory ?? "?")}`}
                data-defect-id={d.id}
                data-active={isActive ? "true" : "false"}
                onClick={() => {
                  onActivate(d.id);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onActivate(d.id);
                  }
                }}
                style={{ cursor: "pointer", outline: "none" }}
              >
                <rect
                  x={d.chordPercent - markerSize / 2}
                  // R58-L-06: y is now (bladeLengthM − r) so r=0 anchors
                  // at the bottom of the viewBox and r=bladeLengthM at
                  // the top.
                  y={yFromRadius(d.radiusM, bladeLengthM) - markerSize / 2}
                  width={markerSize}
                  height={markerSize}
                  fill={fill}
                  stroke={isActive ? palette.primary : palette.border}
                  strokeWidth={isActive ? 1 : 0.3}
                  vectorEffect="non-scaling-stroke"
                />
                <title>
                  Defect · severity {String(d.classCategory ?? "?")} · radius{" "}
                  {d.radiusM.toFixed(1)} m · chord{" "}
                  {String(Math.round(d.chordPercent))} %
                </title>
              </g>
            );
          })}

          {/* Root baseline — drawn at the bottom of the viewBox now that
              R58-L-06 anchors the root there. */}
          <line
            x1={0}
            y1={bladeLengthM - 0.1}
            x2={100}
            y2={bladeLengthM - 0.1}
            stroke={palette.border}
            strokeWidth={0.3}
            vectorEffect="non-scaling-stroke"
          />

          {/* Active-image position cue — drawn last so it overlays the
              defect markers. Same shape as the empty-state version. */}
          {markerY != null ? (
            <g data-testid="sketch-image-marker">
              <line
                x1={0}
                y1={markerY}
                x2={100}
                y2={markerY}
                stroke={palette.primary}
                strokeWidth={0.5}
                strokeDasharray="2 1.5"
                vectorEffect="non-scaling-stroke"
                opacity={0.85}
              />
              <circle
                cx={50}
                cy={markerY}
                r={1.4}
                fill={palette.primary}
                stroke={palette.surface.default}
                strokeWidth={0.4}
                vectorEffect="non-scaling-stroke"
              />
            </g>
          ) : null}
        </svg>
      </Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        sx={{ mt: 0.5, fontSize: 11, color: "text.secondary" }}
        aria-hidden
      >
        {/* Horizontal axis labels = chord (LE/TE). After R58-L-06 the
            radius axis is vertical with root at the bottom and tip at
            the top; its tick labels live inside the SVG via
            <RadiusTicks/>. */}
        <span>LE 0%</span>
        <span>TE 100%</span>
      </Stack>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", textAlign: "center", mt: 0.5 }}
        role="status"
        aria-live="polite"
      >
        {radiusClampedM != null
          ? `Current image · ${radiusClampedM.toFixed(1)} m from root`
          : "Pick an image to see its position"}
      </Typography>
    </Box>
  );
}

interface DensityPaneProps {
  defects: readonly SketchableDefect[];
  bladeLengthM: number;
}

function DensityPane({
  defects,
  bladeLengthM,
}: DensityPaneProps): React.ReactElement {
  const palette = usePalette();
  // A histogram is a chart mark, so its bars take D84's encoding set rather
  // than the brand blue they used to. There is exactly one series here, so it
  // is series 1 — the accent, which is what D84 puts in that slot.
  const barFill = palette.series[0];
  // Group by side; bin radii into 10 equal-width buckets per side.
  const BIN_COUNT = 10;
  const grouped = useMemo(() => {
    const out: Record<SideKey, number[]> = {
      LE: Array.from({ length: BIN_COUNT }, () => 0),
      TE: Array.from({ length: BIN_COUNT }, () => 0),
      PS: Array.from({ length: BIN_COUNT }, () => 0),
      SS: Array.from({ length: BIN_COUNT }, () => 0),
    };
    for (const d of defects) {
      if (!d.side) continue;
      const bin = Math.min(
        BIN_COUNT - 1,
        Math.max(0, Math.floor((d.radiusM / bladeLengthM) * BIN_COUNT)),
      );
      out[d.side][bin] = (out[d.side][bin] ?? 0) + 1;
    }
    return out;
  }, [defects, bladeLengthM]);

  return (
    <Box sx={{ p: 1.5, height: "100%", overflowY: "auto" }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 1,
          height: "100%",
        }}
      >
        {SIDE_ORDER.map((side) => {
          const bins = grouped[side];
          const total = bins.reduce((a, b) => a + b, 0);
          const max = Math.max(1, ...bins);
          return (
            <Box
              key={side}
              data-testid={`density-${side}`}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                p: 1,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Typography
                component="div"
                sx={{
                  fontSize: typography.size["label-md"],
                  fontWeight: typography.weight.semibold,
                  color: "text.primary",
                  mb: 0.5,
                }}
              >
                {side} · {String(total)} defect{total === 1 ? "" : "s"}
              </Typography>
              <svg
                viewBox={`0 0 ${String(BIN_COUNT)} 10`}
                preserveAspectRatio="none"
                style={{ width: "100%", flexGrow: 1 }}
                aria-label={`Density curve for ${side} (${String(total)} defects)`}
              >
                {bins.map((count, i) => {
                  const h = (count / max) * 9;
                  return (
                    <rect
                      key={i}
                      x={i + 0.05}
                      y={10 - h}
                      width={0.9}
                      height={h}
                      fill={barFill}
                      opacity={0.7}
                    />
                  );
                })}
                <line
                  x1={0}
                  y1={10}
                  x2={BIN_COUNT}
                  y2={10}
                  stroke={palette.border}
                  strokeWidth={0.05}
                  vectorEffect="non-scaling-stroke"
                />
              </svg>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

/**
 * Strip the outer `<svg>` wrapper from the BE's outline payload so its
 * children can be inlined into our parent SVG (which sets its own
 * `viewBox`). Best-effort regex — the BE's outline schema is small
 * enough that a full XML parser is overkill, and the FE only renders
 * outline strings sourced from companies_db (trusted).
 */
function stripSvgWrapper(svg: string): string {
  return svg.replace(/^[\s\S]*?<svg[^>]*>/, "").replace(/<\/svg>\s*$/, "");
}

export function SketchWidget({
  inspectionId,
  componentId,
  activeImageRadiusM,
}: SketchWidgetProps & {
  activeImageRadiusM?: number | null;
}): React.ReactElement {
  const [tab, setTab] = useState<"sketch" | "density">("sketch");
  const defectsQuery = useDefectsQuery(inspectionId, { pageSize: 200 });
  const outlineQuery = useComponentOutlineQuery(componentId);
  const activeFindingId = useSelectionStore((s) => s.activeFindingId);
  const setActive = useSelectionStore((s) => s.setActive);

  const blades = useAnalysisFilterStore((s) => s.blades);
  const sides = useAnalysisFilterStore((s) => s.sides);
  const severities = useAnalysisFilterStore((s) => s.severities);
  const statuses = useAnalysisFilterStore((s) => s.statuses);

  const filtered = useMemo(() => {
    const items = defectsQuery.data?.items ?? [];
    const projected = items.map((d) => ({
      ...d,
      bladeId: d.bladeId ?? null,
      side: d.side ?? null,
      classCategory: d.classCategory ?? null,
    }));
    return applyFilters(projected, { blades, sides, severities, statuses });
  }, [defectsQuery.data, blades, sides, severities, statuses]);

  const sketchable = useMemo(() => projectDefects(filtered), [filtered]);

  const bladeLengthM =
    outlineQuery.data?.bladeLengthM ?? FALLBACK_BLADE_LENGTH_M;
  const outlineSvg = outlineQuery.data?.svg ?? null;

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Stack
        direction="row"
        alignItems="center"
        sx={{ borderBottom: 1, borderColor: "divider" }}
      >
        <Tabs
          value={tab}
          onChange={(_, v: "sketch" | "density") => {
            setTab(v);
          }}
          aria-label="Sketch or density view"
          sx={{ minHeight: 32, flexGrow: 1 }}
        >
          <Tab
            label="Sketch"
            value="sketch"
            sx={{ minHeight: 32, py: 0.5 }}
            aria-controls="sketch-panel"
          />
          <Tab
            label="Density"
            value="density"
            sx={{ minHeight: 32, py: 0.5 }}
            aria-controls="density-panel"
          />
        </Tabs>
      </Stack>
      <Box
        id={tab === "sketch" ? "sketch-panel" : "density-panel"}
        role="tabpanel"
        sx={{
          flexGrow: 1,
          minHeight: 0,
        }}
      >
        {tab === "sketch" ? (
          <SketchPane
            defects={sketchable}
            bladeLengthM={bladeLengthM}
            outlineSvg={outlineSvg}
            activeFindingId={activeFindingId}
            onActivate={setActive}
            activeImageRadiusM={activeImageRadiusM ?? null}
          />
        ) : (
          <DensityPane defects={sketchable} bladeLengthM={bladeLengthM} />
        )}
      </Box>
    </Box>
  );
}
